import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import {
  usePutInterventionPreference,
  type UpdateInterventionPreference as ApiUpdateInterventionPreference,
  getGetInterventionPreferencesQueryKey,
  usePutAgreedPlacePicture,
  useDeleteAgreedPlacePicture,
  getGetAgreedPlacePictureQueryKey,
} from '@dhl/receiver-preference-api'
import { useSnackbar } from '@dhl-ui/components'
import { dataLayerPublish } from '@dhl/datalayer'

import type { PreferenceFormValues, UpdateInterventionPreference } from '~/profile/model/preferences/model.js'

type SubmitProps = {
  values: PreferenceFormValues
  initialValues: PreferenceFormValues
  onFailure?: () => void
  onSuccess?: () => void | Promise<void>
}

export const useSubmitPreferencesForm = (): {
  submitPreferredForm: ({ values, initialValues, onFailure, onSuccess }: SubmitProps) => Promise<void>
} => {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync: updatePreferenceAddress } = usePutInterventionPreference()
  const { mutateAsync: uploadAgreedPlacePhoto } = usePutAgreedPlacePicture()
  const { mutateAsync: deleteAgreedPlacePhoto } = useDeleteAgreedPlacePicture()
  const queryClient = useQueryClient()

  const submit = async ({ values, initialValues, onFailure, onSuccess }: SubmitProps) => {
    const { addressId, preference, servicePoint, agreedPlace } = values

    // TODO: [CON-8043] Upload agreed place photo when API is ready
    const body: UpdateInterventionPreference =
      preference === 'servicePoint' || (initialValues.preference === 'servicePoint' && preference === 'ringDoorbell')
        ? {
            preference: {
              psfKey: servicePoint?.servicepoint?.servicepointKey || '',
              name: servicePoint?.servicepoint?.name || '',
              depot: servicePoint?.servicepoint?.depotCode || '',
              active: preference === 'servicePoint',
              interventionType: 'ServicePointInterventionPreference',
            },
          }
        : {
            preference: {
              description: agreedPlace?.description || '',
              active:
                preference === 'agreedPlace' || Boolean(preference === 'ringDoorbell' && agreedPlace?.ringDoorbell),
              interventionType: 'AgreedPlaceInterventionPreference',
              ringDoorbell: Boolean(preference === 'ringDoorbell' && agreedPlace?.ringDoorbell),
            },
          }

    try {
      const result = await updatePreferenceAddress({
        addressId,
        data: body as unknown as ApiUpdateInterventionPreference,
      })

      if (values.agreedPlace?.agreedPlacePhoto) {
        await uploadAgreedPlacePhoto({
          addressId,
          data: values.agreedPlace.agreedPlacePhoto,
        })

        queryClient.setQueryData(getGetAgreedPlacePictureQueryKey(addressId), values.agreedPlace.agreedPlacePhoto)
      } else if (initialValues.agreedPlace?.agreedPlacePhoto && !values.agreedPlace?.agreedPlacePhoto) {
        await deleteAgreedPlacePhoto({ addressId })

        queryClient.removeQueries({
          queryKey: getGetAgreedPlacePictureQueryKey(addressId),
        })
      }

      queryClient.setQueryData(getGetInterventionPreferencesQueryKey(), result)

      onSuccess?.()

      enqueueSnackbar(t('portal.preferences.savedInfoText'), {
        severity: 'success',
      })
    } catch (error) {
      dataLayerPublish('ERROR', { error, page: 'preference-submit' })
      enqueueSnackbar(t('common.pleaseTryAgainErrorMessage'), { severity: 'error' })
      onFailure?.()
    }
  }

  return { submitPreferredForm: submit }
}
